import type { EntityType } from "@config";
import { broadcastEvent } from "../collaboration";
import type {
  CreateEntityFactory,
  CreateEntityFactoryPayloadType,
  CreateEntityType,
  HistoryCreateEntityPayload,
  StoryStateType,
} from "../types";
import {
  applyPromotedCellPlainText,
  promoteCellPlainText,
} from "./promote-cell-plain-text";
import { applyInCellElementMove } from "./apply-in-cell-element-move";
import { deleteChildEntities } from "./deleteEntity";
import { getWholeEntityLayers } from "./getWholeEntityLayers";
import { processNewEntity } from "./processNewEntity";
import { pruneOrphanedCellChildren } from "./prune-orphaned-cell-children";
import { scrollToEntity } from "./scrollToEntity";
import { cleanupAnchors, syncAnchorsForCreatedEntities } from "./syncAnchors";

// =========== CREATE ENTITY ===========
export const createEntityHandler = (
  data: CreateEntityType,
  state: StoryStateType,
): HistoryCreateEntityPayload | undefined => {
  const mapping: Record<
    string,
    (
      data: CreateEntityType,
      state: StoryStateType,
    ) => HistoryCreateEntityPayload | undefined
  > = {
    "slides-library": createEntityFromSlidesLibrary,
    duplicate: duplicateEntity,
    placeholder: createEntityFromPlaceholder,
    "elements-library": createEntityFromElementsLibrary,
    assistant: createEntityFromAssistant,
    shortcut: createEntityFromShortcut,
    "insert-entity": insertFlatEntity,
    "narrator-dialog": createEntityFromNarratorDialog,
    "insert-into-cell": insertIntoCell,
    "move-in-cell-element": moveInCellElement,
    "paste-cells": pasteCells,
  };
  const handler = mapping[data.feature];
  if (!handler) return;
  const historyPayload = handler(data, state);
  return historyPayload;
};

const duplicateEntity = (data: CreateEntityType, state: StoryStateType) => {
  if (data.feature !== "duplicate") return;
  const id = data.id || state.activeEntityId || "";

  const wholeEntity = getWholeEntityLayers(id, state.flatContentLayers);
  const activeEntity = state.flatContentLayers[id];
  if (!activeEntity || !wholeEntity?.root) return;
  const parentId = activeEntity.parentId as string;
  // An explicit index wins. Anchoring works by "insert after this slide",
  // which cannot express "insert at the front" -- an empty anchor falls back
  // to the ACTIVE entity above, so the slide lands after whatever happens to
  // be selected. A caller that knows the exact slot says so instead. See
  // how-to-start/deck-build-runner, which places generated slides at the
  // positions their outline gave them, in whatever order they arrive.
  const index =
    typeof data.index === "number"
      ? data.index
      : state.flatContentLayers[parentId].childIds.indexOf(id) + 1;
  const created = createEntityFactory({
    processEntityPayload: {
      entityEntryRootId: wholeEntity.root,
      entities: wholeEntity.entities,
      parentId,
    },
    entitySettings: { id, parentId, index },
    state,
    broadcast: data.broadcast,
    broadcastPayload: data.broadcastPayload,
  });

  if (!data.broadcast) {
    handleBroadcast(state, {
      feature: "duplicate",
      id,
      index,
      broadcastPayload: created,
    });
  }

  return {
    index,
    parentId,
    rootId: created?.root?.id || "",
    entities: created?.entities || {},
  };
};

const createEntityFromSlidesLibrary = (
  data: CreateEntityType,
  state: StoryStateType,
) => {
  if (data.feature !== "slides-library") return;
  const { entities, entityEntryRootId, broadcast, broadcastPayload } = data;
  const id = data.id || state.activeEntityId || "";
  const parentId = "root";
  const childIds = state.flatContentLayers[parentId].childIds;
  // THE ANCHOR IS RARELY A SLIDE, and root's children are only slides.
  //
  // The anchor falls back to the ACTIVE entity, which is almost never a slide
  // -- click into a heading and it is that RichText. `childIds.indexOf` then
  // answers -1 for it, -1 + 1 is 0, and the new slide goes to the FRONT of the
  // deck instead of below the one being worked on. That is the "sometimes it
  // adds at the top" bug, and it is intermittent only because it depends on
  // what happens to be selected. A stale anchor -- a slide since deleted, or
  // one from another story -- lands on exactly the same -1.
  //
  // So the anchor is resolved to the slide that OWNS it, and an anchor that
  // resolves to nothing appends. Wherever the slide belongs, the top of
  // somebody's deck is the one place it must not silently go.
  const anchorSlideId = findParentSlideId(state, id);
  const anchorIndex = anchorSlideId ? childIds.indexOf(anchorSlideId) : -1;
  // An explicit index wins: a caller that knows the exact slot says so. See
  // how-to-start/deck-build-runner, which places generated slides at the
  // positions their outline gave them, in whatever order they arrive.
  const index =
    typeof data.index === "number"
      ? data.index
      : anchorIndex >= 0
        ? anchorIndex + 1
        : childIds.length;
  const created = createEntityFactory({
    processEntityPayload: { entityEntryRootId, entities, parentId },
    entitySettings: { id, parentId, index },
    state,
    broadcast,
    broadcastPayload,
    // Insert without selecting and scrolling to the new slide. Set while a
    // deck streams into an editor the user has already started working in.
    skipActiveEntity: data.skipActiveEntity,
  });

  if (!broadcast) {
    handleBroadcast(state, {
      feature: "slides-library",
      entities,
      entityEntryRootId,
      id,
      index,
      broadcastPayload: created,
    });
  }

  return {
    index,
    parentId,
    rootId: created?.root?.id || "",
    entities: created?.entities || {},
  };
};

const createEntityFromPlaceholder = (
  data: CreateEntityType,
  state: StoryStateType,
) => {
  if (data.feature !== "placeholder") return;
  const { entityEntryRootId, entities, parentId, broadcast, broadcastPayload } =
    data;
  const created = createEntityFactory({
    processEntityPayload: { entityEntryRootId, entities, parentId },
    entitySettings: { id: "", parentId, index: 1 },
    state,
    broadcast,
    broadcastPayload,
  });

  if (!broadcast) {
    handleBroadcast(state, {
      feature: "placeholder",
      entities,
      entityEntryRootId,
      parentId,
      index: 1,
      broadcastPayload: created,
    });
  }

  return {
    index: 0,
    parentId,
    rootId: created?.root?.id || "",
    entities: created?.entities || {},
  };
};

const createEntityFromElementsLibrary = (
  data: CreateEntityType,
  state: StoryStateType,
) => {
  if (data.feature !== "elements-library") return;

  const {
    id,
    parentId,
    entityEntryRootId,
    entities,
    placement,
    broadcast,
    broadcastPayload,
  } = data;

  const entity = state.flatContentLayers[id];
  const parent = state.flatContentLayers[entity?.parentId || parentId || ""];
  if (!parent) return;

  const currentIndex = parent.childIds.indexOf(id);
  const index = placement === "top" ? currentIndex : currentIndex + 1;
  const created = createEntityFactory({
    processEntityPayload: { entityEntryRootId, entities, parentId },
    entitySettings: { id, parentId, index },
    state,
    broadcast,
    broadcastPayload,
    skipActiveEntity: true,
  });

  if (!broadcast) {
    if (
      created?.root?.type === "combo" &&
      created?.root?.settings.name === "Simple"
    ) {
      const element = Object.values(created?.entities || {}).find(
        (entity) => entity.type === "element",
      );
      if (element) handleActiveEntity(state, element);
    } else {
      if (created?.root) handleActiveEntity(state, created.root);
    }
  }

  if (!broadcast) {
    handleBroadcast(state, {
      feature: "elements-library",
      entityEntryRootId,
      entities,
      id,
      parentId,
      placement,
      index,
      broadcastPayload: created,
    });
  }

  return {
    index,
    parentId,
    rootId: created?.root?.id || "",
    entities: created?.entities || {},
  };
};

const createEntityFromAssistant = (
  data: CreateEntityType,
  state: StoryStateType,
) => {
  if (data.feature !== "assistant") return;
  const processEntityPayload = {} as CreateEntityFactoryPayloadType;
  const entities = state.flatContentLayers;
  const { entityEntryRootId, type, broadcast, broadcastPayload } = data;
  const activeTopLayerType = getActiveTopLayerType(
    entities,
    state.activeEntityId,
    type,
  );
  if (!activeTopLayerType) return;
  const { parentId, id } = activeTopLayerType as {
    parentId: string;
    id: string;
  };
  const initial = getWholeEntityLayers(id, entities);
  Object.assign(processEntityPayload, {
    entityEntryRootId,
    entities: data.entities,
    parentId,
  });
  const { root, entities: processed } =
    broadcastPayload || processNewEntity(processEntityPayload);
  if (!root) return;
  const historyPayload = {
    index: 0,
    parentId,
    rootId: root.id,
    entities: processed,
    activeEntityId: "",
    initialEntities: initial?.entities,
    initialRootId: initial?.root,
    type: "assistant",
  };

  let index: number | undefined;
  if (type === "slide") {
    index = entities[parentId].childIds.indexOf(id);
    entities[parentId].childIds.splice(index, 1, root.id);
    Object.assign(historyPayload, { index, parentId: "root" });
  } else {
    index = entities[parentId]?.childIds.indexOf(id);
    if (index === undefined) return;
    entities[parentId].childIds.splice(index, 1, root.id);
    Object.assign(historyPayload, { index, parentId });
  }

  cleanupAnchors(state, initial?.entities || {});
  Object.keys(initial?.entities || {}).forEach((key) => {
    delete entities[key];
  });
  state.flatContentLayers = { ...entities, ...processed };
  syncAnchorsForCreatedEntities(state, processed);

  if (!broadcast) {
    handleActiveEntity(state, root);
    handleBroadcast(state, {
      feature: "assistant",
      entities: data.entities,
      entityEntryRootId,
      type,
      index,
      broadcastPayload: { root, entities: processed },
    });
  } else {
    const hasActive = entities[state.activeEntityId || ""];
    if (!hasActive) handleActiveEntity(state, root);
  }

  Object.assign(historyPayload, { activeEntityId: state.activeEntityId });
  return historyPayload;
};

const createEntityFromShortcut = (
  data: CreateEntityType,
  state: StoryStateType,
) => {
  if (data.feature !== "shortcut") return;
  const {
    entities,
    entityEntryRootId,
    id,
    placement,
    broadcast,
    broadcastPayload,
  } = data;
  const activeId = id || state.activeEntityId || "";
  const entity = state.flatContentLayers[activeId];
  const payloadRoot = entities[entityEntryRootId];
  if (!entity || !payloadRoot) return;
  const parentId = entity.parentId || "";
  if (entity.type === "combo" && payloadRoot.type === "slide") {
    // A slide payload may be inserted next to a combo.
  } else if (entity.type !== payloadRoot.type) return;
  const currentIndex =
    state.flatContentLayers[parentId].childIds.indexOf(activeId);
  const index = placement === "top" ? currentIndex : currentIndex + 1;
  const created = createEntityFactory({
    processEntityPayload: { entityEntryRootId, entities, parentId },
    entitySettings: { id: activeId, parentId, index },
    state,
    broadcast,
    broadcastPayload,
  });

  if (!data.broadcast) {
    handleBroadcast(state, {
      feature: "shortcut",
      entities,
      entityEntryRootId,
      id: activeId,
      index,
      broadcastPayload: created,
    });
  }

  return {
    index,
    parentId,
    rootId: created?.root?.id || "",
    entities: created?.entities || {},
  };
};

const insertFlatEntity = (data: CreateEntityType, state: StoryStateType) => {
  if (data.feature !== "insert-entity") return;
  const { entities, entityEntryRootId, index } = data;
  const root = entities[entityEntryRootId];
  const parentId = root?.parentId;
  if (!parentId) return;
  state.flatContentLayers = { ...state.flatContentLayers, ...entities };
  syncAnchorsForCreatedEntities(state, entities);
  const parent = state.flatContentLayers[parentId];
  parent.childIds.splice(index, 0, entityEntryRootId);

  if (!data.broadcast) {
    handleBroadcast(state, {
      feature: "insert-entity",
      entities,
      entityEntryRootId,
      index,
    });
  }

  return {
    index,
    parentId,
    rootId: entityEntryRootId,
    entities,
  };
};

const createEntityFromNarratorDialog = (
  data: CreateEntityType,
  state: StoryStateType,
) => {
  if (data.feature !== "narrator-dialog") return;
  const { entities, entityEntryRootId } = data;
  const root = entities[entityEntryRootId];
  const initial = getWholeEntityLayers(
    entityEntryRootId,
    state.flatContentLayers,
  );
  const parentId = root?.parentId;
  if (!parentId) return;
  cleanupAnchors(state, initial?.entities || {});
  state.flatContentLayers = { ...state.flatContentLayers, ...entities };
  syncAnchorsForCreatedEntities(state, entities);
  const index =
    state.flatContentLayers[parentId].childIds.indexOf(entityEntryRootId);
  const parent = state.flatContentLayers[parentId];
  parent.childIds[index] = entityEntryRootId;

  if (!data.broadcast) {
    handleBroadcast(state, {
      feature: "narrator-dialog",
      entities,
      entityEntryRootId,
      index,
    });
  } else {
    const hasActive = entities[state.activeEntityId || ""];
    if (!hasActive) handleActiveEntity(state, root);
  }

  return {
    parentId,
    rootId: entityEntryRootId,
    entities,
    index,
    initialEntities: initial?.entities,
    initialRootId: initial?.root,
    type: "narrator-dialog",
  };
};

// Tables 2.0 in-cell elements: one reducer pass registers the new element
// subtrees, splices their roots into table.childIds and (optionally) rewrites
// the cell's text content — one history step, one broadcast.
const insertIntoCell = (data: CreateEntityType, state: StoryStateType) => {
  if (data.feature !== "insert-into-cell") return;
  const { entities, rootIds, tableEntityId, cellId, content, priorContent } =
    data;
  const table = state.flatContentLayers[tableEntityId];
  if (!table || !rootIds.length) return;
  const tableData = table.settings.tableData as
    | Array<{ id: string; content: string; mergedInto?: string }>
    | undefined;
  const cell = tableData?.find((c) => c.id === cellId && !c.mergedInto);
  if (!cell) return;

  state.flatContentLayers = { ...state.flatContentLayers, ...entities };
  syncAnchorsForCreatedEntities(state, entities);
  const index =
    data.index === undefined || data.index > table.childIds.length
      ? table.childIds.length
      : data.index;
  table.childIds.splice(index, 0, ...rootIds);
  if (content !== undefined) cell.content = content;

  if (!data.broadcast) {
    // The cell is already on screen — activate without the scroll dance.
    state.activeEntityId = rootIds[rootIds.length - 1];
    handleBroadcast(state, {
      feature: "insert-into-cell",
      entities,
      rootIds,
      tableEntityId,
      cellId,
      index,
      content,
      priorContent,
      // Post-patch tableData for the Yjs mapper's edit op (a peer's Redux
      // context is pre-mutation, so the payload must carry the new value).
      ...(content !== undefined && {
        tableData: table.settings.tableData,
      }),
    });
  }

  return {
    index,
    parentId: tableEntityId,
    rootId: rootIds[0],
    entities,
    type: "insert-into-cell",
    rootIds,
    tableEntityId,
    cellId,
    content,
    priorContent,
  } as HistoryCreateEntityPayload;
};

// Tables 2.0 clipboard paste — see IPasteCellsPlan. The plan is fully
// precomputed; this handler only swaps grid fields, registers subtrees and
// prunes (safety net: with fill-only merges nothing should ever be orphaned).
const pasteCells = (data: CreateEntityType, state: StoryStateType) => {
  if (data.feature !== "paste-cells") return;
  const { entities, rootIds, tableEntityId, removeRootIds } = data;
  const table = state.flatContentLayers[tableEntityId];
  if (!table) return;
  const s = table.settings;
  const prior = {
    tableData: s.tableData,
    tableCols: s.tableCols as number,
    tableRows: s.tableRows as number,
    tableColWidths: s.tableColWidths as number[],
    tableRowHeights: s.tableRowHeights as number[],
  };

  // Span-replace: the target cells' pre-existing in-cell subtrees can't
  // coexist with the incoming source content, so drop them here — before the
  // new subtrees are registered — capturing pre-removal indexes the same way
  // pruneOrphanedCellChildren does so undo can splice them back.
  const removeSet = new Set(removeRootIds);
  const removedIndexes: Record<string, number> = {};
  table.childIds.forEach((childId, index) => {
    if (removeSet.has(childId)) removedIndexes[childId] = index;
  });
  const removedRootIds = Object.keys(removedIndexes);
  const removedEntities: Record<string, EntityType> = {};
  removedRootIds.forEach((id) => {
    const child = state.flatContentLayers[id];
    if (child)
      deleteChildEntities(child, state.flatContentLayers, removedEntities);
  });
  if (removedRootIds.length) {
    table.childIds = table.childIds.filter((id) => !(id in removedIndexes));
  }

  s.tableData = data.tableData;
  s.tableCols = data.tableCols;
  s.tableRows = data.tableRows;
  s.tableColWidths = data.tableColWidths;
  s.tableRowHeights = data.tableRowHeights;

  state.flatContentLayers = { ...state.flatContentLayers, ...entities };
  syncAnchorsForCreatedEntities(state, entities);
  table.childIds.push(...rootIds);
  const pruned = pruneOrphanedCellChildren(state, tableEntityId);

  if (!data.broadcast) {
    handleBroadcast(state, {
      feature: "paste-cells",
      tableEntityId,
      entities,
      rootIds,
      // Actually-removed ids (a requested id not found in childIds is
      // dropped) so the Yjs mapper emits matching delete ops for peers.
      removeRootIds: removedRootIds,
      tableData: s.tableData,
      tableCols: s.tableCols as number,
      tableRows: s.tableRows as number,
      tableColWidths: s.tableColWidths as number[],
      tableRowHeights: s.tableRowHeights as number[],
      // Pruned subtree ids so the Yjs mapper emits matching delete ops —
      // without them the Y.Doc keeps the orphans and reconcile resurrects.
      ...(pruned && { prunedChildIds: Object.keys(pruned.entities) }),
    });
  }

  return {
    index: table.childIds.length - rootIds.length,
    parentId: tableEntityId,
    rootId: rootIds[0] ?? tableEntityId,
    entities,
    type: "paste-cells",
    tableEntityId,
    rootIds,
    next: {
      tableData: s.tableData,
      tableCols: s.tableCols as number,
      tableRows: s.tableRows as number,
      tableColWidths: s.tableColWidths as number[],
      tableRowHeights: s.tableRowHeights as number[],
    },
    prior,
    ...(removedRootIds.length ? { removedEntities, removedIndexes } : {}),
    ...(pruned
      ? { prunedEntities: pruned.entities, prunedIndexes: pruned.indexes }
      : {}),
  } as HistoryCreateEntityPayload;
};

// Tables 2.0 in-cell DnD (Task 5): reposition an existing in-cell element via
// Task 3's pure apply — one history step, one broadcast. When the target cell
// still holds native text, promote it to a RichText child first (same shape as
// insert-into-cell) so the former text band becomes a reorderable sibling.
const moveInCellElement = (data: CreateEntityType, state: StoryStateType) => {
  if (data.feature !== "move-in-cell-element") return;
  const { elementId, toContainerId, toCellId, index } = data;

  let promote: ReturnType<typeof promoteCellPlainText> | undefined;
  if (toCellId) {
    if (
      data.broadcast &&
      data.promoteEntities &&
      data.promoteRootIds?.length &&
      typeof data.promoteIndex === "number" &&
      data.content !== undefined
    ) {
      applyPromotedCellPlainText(state, {
        tableId: toContainerId,
        cellId: toCellId,
        entities: data.promoteEntities,
        rootIds: data.promoteRootIds,
        index: data.promoteIndex,
        content: data.content,
      });
      promote = {
        richTextId: data.promoteRootIds[0],
        entities: data.promoteEntities,
        rootIds: data.promoteRootIds,
        content: data.content,
        priorContent: data.priorContent ?? "",
        index: data.promoteIndex,
        tableData: (data.tableData as never) ?? [],
      };
    } else if (!data.broadcast) {
      promote = promoteCellPlainText(state, toContainerId, toCellId);
      if (promote) {
        syncAnchorsForCreatedEntities(state, promote.entities);
        // Froala may still hold the pre-promotion text while the cell is
        // active — push the emptied content so it can't save stale text back.
        window.__CONTENT_TRIGGER_UPDATE__?.(
          `${toContainerId}-${toCellId}`,
          promote.content,
        );
        window.__CONTENT_TRIGGER_UPDATE__?.(
          `${toContainerId}-table-froala`,
          promote.content,
        );
      }
    }
  }

  const inverse = applyInCellElementMove(state, {
    elementId,
    toContainerId,
    toCellId,
    index,
  });
  if (!inverse) return;

  if (!data.broadcast) {
    state.activeEntityId = elementId;
    handleBroadcast(state, {
      feature: "move-in-cell-element",
      elementId,
      toContainerId,
      toCellId,
      index,
      fromCellId: inverse.fromCellId,
      ...(promote && {
        promoteEntities: promote.entities,
        promoteRootIds: promote.rootIds,
        promoteIndex: promote.index,
        content: promote.content,
        priorContent: promote.priorContent,
        tableData: promote.tableData,
      }),
    });
  }

  return {
    index,
    parentId: toContainerId,
    rootId: elementId,
    // History needs the promoted RichText entities so undo can delete them.
    entities: promote?.entities ?? {},
    type: "move-in-cell-element",
    elementId,
    toContainerId,
    toCellId,
    fromContainerId: inverse.fromContainerId,
    fromCellId: inverse.fromCellId,
    fromIndex: inverse.fromIndex,
    ...(promote && {
      promoteRootIds: promote.rootIds,
      content: promote.content,
      priorContent: promote.priorContent,
    }),
  } as HistoryCreateEntityPayload;
};

// ========= HELPERS =========
const createEntityFactory = ({
  processEntityPayload,
  state,
  entitySettings,
  broadcast,
  broadcastPayload,
  skipActiveEntity,
}: CreateEntityFactory) => {
  const updated = broadcastPayload || processNewEntity(processEntityPayload);
  if (!updated.root) return;

  // ===== tap to reset default tab =====
  if (updated.root.settings.isTabDefault) {
    updated.root.settings.isTabDefault = false;
    updated.entities[updated.root.id] = updated.root;
  }
  // ===== tap to reset default tab =====
  const parent = state.flatContentLayers[entitySettings.parentId];
  parent.childIds.splice(entitySettings.index, 0, updated.root.id);
  state.flatContentLayers = { ...state.flatContentLayers, ...updated.entities };
  syncAnchorsForCreatedEntities(state, updated.entities);
  if (broadcast || skipActiveEntity) return updated;
  handleActiveEntity(state, updated.root);
  return updated;
};

const getActiveTopLayerType = (
  entities: Record<string, EntityType>,
  activeEntityId: string | null,
  type: "slide" | "combo" = "slide",
): EntityType | null => {
  const activeEntity = entities[activeEntityId || ""];
  if (activeEntity.type === "slide" && type === "combo")
    return entities[activeEntity.childIds[0]];
  if (!activeEntity) return null;
  if (activeEntity.type === type) return activeEntity;
  return getActiveTopLayerType(entities, activeEntity.parentId || "", type);
};

const findParentSlideId = (
  state: StoryStateType,
  entityId: string,
): string | null => {
  const entity = state.flatContentLayers[entityId];
  if (!entity) return null;
  if (entity.type === "slide") return entity.id;
  if (entity.parentId) return findParentSlideId(state, entity.parentId);
  return null;
};

const handleActiveEntity = (state: StoryStateType, entity: EntityType) => {
  state.activeEntityId = entity.id;
  const slideId = findParentSlideId(state, entity.id);
  if (slideId) state.activeThumbnailId = slideId;
  handleScrollToActiveEntity(state);
};

const handleScrollToActiveEntity = (state: StoryStateType) => {
  const id = state.activeEntityId;
  if (!id) return;
  state.thumbnailObserverStatus = "disabled";
  scrollToEntity(id);
};

const handleBroadcast = (state: StoryStateType, data: CreateEntityType) => {
  broadcastEvent({
    state,
    event: "create-entity",
    payload: data,
  });
};

// ========= HELPERS =========
